import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { db } from "../../../firebase"; // Primary DB (Orders & Auth)
import { erpDb } from "../../../secondaryFirebase"; // Secondary DB (ERP Ledgers)
import {
  FaCoins,
  FaWallet,
  FaSyncAlt,
  FaSignOutAlt,
  FaEdit,
  FaUnlink,
  FaBuilding,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaFileInvoice,
  FaShieldAlt,
  FaSpinner,
  FaLink,
  FaExclamationTriangle,
} from "react-icons/fa";
import "./RetailerProfile.scss";

const RetailerProfile = () => {
  const navigate = useNavigate();
  const editFormRef = useRef(null); // For smooth scrolling to edit form

  const session = useMemo(() => {
    const stored = localStorage.getItem("retailerSession");
    return stored ? JSON.parse(stored) : null;
  }, []);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState("");

  // Profile State (From Primary DB)
  const [profileData, setProfileData] = useState({});
  const [editForm, setEditForm] = useState({});

  // Ledger State (From Secondary ERP DB)
  const [balances, setBalances] = useState({ metal: 0, amount: 0 });
  const [erpConnectionStatus, setErpConnectionStatus] = useState("Checking...");

  const loadData = useCallback(async () => {
    if (!session?.id) return navigate("/retailer-login");

    try {
      const profileSnap = await getDoc(doc(db, "parties", session.id));
      let currentProfile = {};

      if (profileSnap.exists()) {
        const pData = profileSnap.data();
        currentProfile = {
          name: pData.name || session.name || "Retailer",
          contactPerson: pData.contactPerson || "",
          phone: pData.phone || session.phone || "",
          email: pData.email || "",
          address: pData.address || "",
          gstNumber: pData.gstNumber || "",
          panNumber: pData.panNumber || "",
          status: pData.status || "Active",
          erpPartyId: pData.erpPartyId || "",
        };
        setProfileData(currentProfile);
        setEditForm(currentProfile);
      }

      await syncWithErp(currentProfile);
    } catch (err) {
      console.error("Data load failed:", err);
      setErpConnectionStatus("Connection Failed");
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [session, navigate]);

  const syncWithErp = async (profile) => {
    try {
      setErpConnectionStatus("Locating ERP Ledger...");

      const compSnap = await getDocs(collection(erpDb, "companies"));
      if (compSnap.empty) {
        setErpConnectionStatus("ERP Company Not Found");
        return;
      }
      const company = compSnap.docs[0];
      const companyId = company.id;

      const fySnap = await getDocs(
        collection(erpDb, "companies", companyId, "financialYears"),
      );
      const fyList = fySnap.docs.map((d) => d.id).sort();
      const fy = company.data().currentActiveFY || fyList[fyList.length - 1];

      if (!fy) return;

      let targetErpId = profile.erpPartyId;

      // 🔑 AUTO-DISCOVERY ENGINE: STRICT GSTIN MATCHING
      if (!targetErpId) {
        const primaryGst = (profile.gstNumber || "").trim().toUpperCase();

        if (primaryGst) {
          setErpConnectionStatus("Searching ERP by GSTIN...");
          const allErpPartiesSnap = await getDocs(
            collection(erpDb, "companies", companyId, "parties"),
          );

          for (const pDoc of allErpPartiesSnap.docs) {
            const pData = pDoc.data();
            const erpGst = (pData.gstin || "").trim().toUpperCase();

            if (erpGst && primaryGst === erpGst) {
              targetErpId = pDoc.id;
              break;
            }
          }

          if (targetErpId) {
            try {
              await updateDoc(doc(db, "parties", session.id), {
                erpPartyId: targetErpId,
              });
            } catch (primaryDbError) {
              console.warn("Primary DB write blocked, proceeding in-memory.");
            }

            setProfileData((prev) => ({ ...prev, erpPartyId: targetErpId }));
            setEditForm((prev) => ({ ...prev, erpPartyId: targetErpId }));
          }
        }
      }

      if (!targetErpId) {
        setErpConnectionStatus("Not Linked to Factory ERP");
        setBalances({ metal: 0, amount: 0 });
        setLastSyncTime(
          new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        );
        return;
      }

      setErpConnectionStatus("Linked & Synced");

      const erpPartySnap = await getDoc(
        doc(erpDb, "companies", companyId, "parties", targetErpId),
      );
      const fyPartySnap = await getDoc(
        doc(
          erpDb,
          "companies",
          companyId,
          "financialYears",
          fy,
          "parties",
          targetErpId,
        ),
      );

      const party = erpPartySnap.exists() ? erpPartySnap.data() : {};
      const fyData = fyPartySnap.exists() ? fyPartySnap.data() : {};

      const openingGoldInt =
        fyData.openingGoldBalanceInt !== undefined
          ? fyData.openingGoldBalanceInt
          : party.previousGoldBalancePureInt || 0;
      const openingGold = Number(openingGoldInt) / 1000;

      let openingAmtInt = 0;
      if (fyData.openingApprovalAmountBalanceInt !== undefined)
        openingAmtInt = fyData.openingApprovalAmountBalanceInt;
      else if (fyData.openingAmountBalanceInt !== undefined)
        openingAmtInt = fyData.openingAmountBalanceInt;
      else if (party.previousApprovalAmountBalanceInt !== undefined)
        openingAmtInt = party.previousApprovalAmountBalanceInt;
      else openingAmtInt = party.previousAmountBalanceInt || 0;
      const openingAmt = Number(openingAmtInt) / 100;

      let issueMetal = 0,
        receiveMetal = 0,
        amountTotalDelta = 0;
      const ledgerSnap = await getDocs(
        collection(
          erpDb,
          "companies",
          companyId,
          "financialYears",
          fy,
          "parties",
          targetErpId,
          "approvalLedger",
        ),
      );

      ledgerSnap.forEach((d) => {
        const v = d.data();
        issueMetal += Number(v.goldIssueFine || 0);
        receiveMetal += Number(v.goldReceivedFine || 0);
        amountTotalDelta += Number(v.amountTotal || 0);
      });

      // 🔥 MATHEMATICALLY REVERSED BALANCES
      const finalMetal = -(openingGold + issueMetal - receiveMetal);
      const finalAmount = -(openingAmt + amountTotalDelta);

      setBalances({
        metal: finalMetal,
        amount: finalAmount,
      });
      setLastSyncTime(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
    } catch (error) {
      console.warn(
        "ERP Sync Notice: Could not calculate secondary ledgers",
        error,
      );
      setErpConnectionStatus("Sync Error");
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleManualSync = () => {
    setSyncing(true);
    syncWithErp(profileData).then(() => setSyncing(false));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSyncing(true);
    try {
      await updateDoc(doc(db, "parties", session.id), editForm);
      setProfileData({ ...editForm });
      setIsEditing(false);
      await syncWithErp(editForm);
    } catch (err) {
      alert("Failed to update profile.");
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Securely sign out of your terminal?")) {
      localStorage.removeItem("retailerSession");
      navigate("/retailer-login");
    }
  };

  const scrollToEdit = () => {
    setIsEditing(true);
    setTimeout(() => {
      editFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const formatMetal = (val) => {
    if (val === 0) return "0.000";
    const sign = val > 0 ? "+" : "-";
    return `${sign} ${Math.abs(val).toFixed(3)}`;
  };

  const formatAmount = (val) => {
    if (val === 0) return "₹0.00";
    const sign = val > 0 ? "+" : "-";
    return `${sign} ₹${Math.abs(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  };

  if (loading)
    return (
      <div className="wx-loading-screen">
        <FaSpinner className="spin" />
        <p>Connecting to Factory ERP...</p>
      </div>
    );

  const isLinked =
    profileData.erpPartyId && erpConnectionStatus === "Linked & Synced";

  return (
    <div className="wx-profile-wrapper fade-in">
      {/* HEADER / NAVIGATION */}
      <header className="wx-header slide-down">
        <div className="profile-identity">
          <div className="avatar">
            {profileData.name?.slice(0, 1).toUpperCase()}
            <div
              className={`status-dot ${isLinked ? "online" : "offline"}`}
            ></div>
          </div>
          <div className="info">
            <h1>{profileData.name}</h1>
            <span className="subtitle">
              {isLinked ? (
                <>
                  <FaShieldAlt className="icon-safe" /> Verified Partner
                </>
              ) : (
                "Unverified Account"
              )}
            </span>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-icon" onClick={handleLogout} title="Sign Out">
            <FaSignOutAlt />
          </button>
        </div>
      </header>

      {/* LAST SYNC INDICATOR */}
      <div className="wx-sync-banner">
        <span>Last updated: {lastSyncTime}</span>
        <button
          className={`btn-sync-text ${syncing ? "spinning" : ""}`}
          onClick={handleManualSync}
        >
          <FaSyncAlt /> Sync Now
        </button>
      </div>

      {/* BALANCES SECTION */}
      <main className="wx-main-content">
        <section className="wx-balance-stack scale-in">
          {/* METAL CARD */}
          <div
            className={`wx-card premium-card metal-card ${!isLinked ? "unlinked-blur" : ""}`}
          >
            <div className="card-top">
              <div className="icon-wrapper">
                <FaCoins />
              </div>
              <div
                className={`connection-pill ${isLinked ? "success" : "error"}`}
              >
                {isLinked ? <FaLink /> : <FaExclamationTriangle />}
                {isLinked ? "Live" : "No ERP Link"}
              </div>
            </div>

            <div className="card-body">
              <p className="metric-label">FINE GOLD BALANCE (24K)</p>
              <div className="metric-value-row">
                <h2
                  className={
                    balances.metal < 0
                      ? "text-urgent"
                      : balances.metal > 0
                        ? "text-ready"
                        : "text-neutral"
                  }
                >
                  {isLinked ? formatMetal(balances.metal) : "0.000"}
                </h2>
                <span className="unit">gm</span>
              </div>
            </div>

            <div className="card-footer">
              <div
                className={`status-bar ${balances.metal < 0 ? "bg-urgent" : balances.metal > 0 ? "bg-ready" : "bg-neutral"}`}
              >
                {balances.metal < 0
                  ? "Action: Payable to Factory"
                  : balances.metal > 0
                    ? "Status: Advance at Factory"
                    : "Account Settled"}
              </div>
            </div>

            {!isLinked && (
              <div className="unlinked-overlay">
                <div className="overlay-content">
                  <FaUnlink className="huge-icon" />
                  <h3>Gold Ledger Unavailable</h3>
                  <p>Your GSTIN is required to sync balances.</p>
                  <button className="btn-primary-sm" onClick={scrollToEdit}>
                    Update Profile
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* AMOUNT CARD */}
          <div
            className={`wx-card premium-card amount-card ${!isLinked ? "unlinked-blur" : ""}`}
          >
            <div className="card-top">
              <div className="icon-wrapper">
                <FaWallet />
              </div>
              <div
                className={`connection-pill ${isLinked ? "success" : "error"}`}
              >
                {isLinked ? <FaLink /> : <FaExclamationTriangle />}
                {isLinked ? "Live" : "No ERP Link"}
              </div>
            </div>

            <div className="card-body">
              <p className="metric-label">SETTLEMENT BALANCE</p>
              <div className="metric-value-row">
                <h2
                  className={
                    balances.amount < 0
                      ? "text-urgent"
                      : balances.amount > 0
                        ? "text-ready"
                        : "text-neutral"
                  }
                >
                  {isLinked ? formatAmount(balances.amount) : "₹0.00"}
                </h2>
              </div>
            </div>

            <div className="card-footer">
              <div
                className={`status-bar ${balances.amount < 0 ? "bg-urgent" : balances.amount > 0 ? "bg-ready" : "bg-neutral"}`}
              >
                {balances.amount < 0
                  ? "Action: Debit (You Owe Factory)"
                  : balances.amount > 0
                    ? "Status: Credit (Factory Owes You)"
                    : "Account Settled"}
              </div>
            </div>

            {!isLinked && (
              <div className="unlinked-overlay">
                <div className="overlay-content">
                  <FaUnlink className="huge-icon" />
                  <h3>Financial Ledger Unavailable</h3>
                  <button className="btn-primary-sm" onClick={scrollToEdit}>
                    Update Profile
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* DETAILS SECTION */}
        <section className="wx-details-section slide-up" ref={editFormRef}>
          <div className="section-header">
            <h2>Business Profile</h2>
            {!isEditing && (
              <button
                className="btn-edit-text"
                onClick={() => setIsEditing(true)}
              >
                <FaEdit /> Edit
              </button>
            )}
          </div>

          {!isEditing ? (
            <div className="wx-card info-card">
              <ul className="info-list">
                <li>
                  <div className="icon">
                    <FaBuilding />
                  </div>
                  <div className="data">
                    <span className="label">Firm Name</span>
                    <span className="value">{profileData.name}</span>
                  </div>
                </li>
                <li>
                  <div className="icon">
                    <FaPhoneAlt />
                  </div>
                  <div className="data">
                    <span className="label">Registered Mobile</span>
                    <span className="value">
                      {profileData.phone || "Not Set"}
                    </span>
                  </div>
                </li>
                <li>
                  <div className="icon">
                    <FaFileInvoice />
                  </div>
                  <div className="data">
                    <span className="label">GSTIN (For ERP Link)</span>
                    <span className="value mono">
                      {profileData.gstNumber || "Not Set"}
                    </span>
                  </div>
                </li>
                <li>
                  <div className="icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="data">
                    <span className="label">Delivery Address</span>
                    <span className="value address">
                      {profileData.address || "No address provided."}
                    </span>
                  </div>
                </li>
                {profileData.erpPartyId && (
                  <li>
                    <div className="icon">
                      <FaLink style={{ color: "#10b981" }} />
                    </div>
                    <div className="data">
                      <span className="label">Factory ERP ID</span>
                      <span className="value mono text-ready">
                        {profileData.erpPartyId}
                      </span>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          ) : (
            <div className="wx-card form-card">
              <form onSubmit={handleSaveProfile}>
                <div className="form-notice">
                  <FaExclamationTriangle />
                  <p>
                    Your <strong>GSTIN</strong> must exactly match the Factory's
                    records to automatically link your live balances.
                  </p>
                </div>

                <div className="input-group">
                  <label>GSTIN (Crucial for linking)</label>
                  <input
                    type="text"
                    placeholder="e.g. 27AAAAA0000A1Z5"
                    value={editForm.gstNumber}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        gstNumber: e.target.value.toUpperCase(),
                      })
                    }
                    className="highlight-input"
                  />
                </div>

                <div className="input-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    value={editForm.contactPerson}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        contactPerson: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="input-group">
                  <label>Mobile Number (WhatsApp)</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phone: e.target.value })
                    }
                  />
                </div>

                <div className="input-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm({ ...editForm, email: e.target.value })
                    }
                  />
                </div>

                <div className="input-group">
                  <label>PAN Number</label>
                  <input
                    type="text"
                    value={editForm.panNumber}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        panNumber: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </div>

                <div className="input-group">
                  <label>Complete Address</label>
                  <textarea
                    rows="3"
                    value={editForm.address}
                    onChange={(e) =>
                      setEditForm({ ...editForm, address: e.target.value })
                    }
                  />
                </div>

                <div className="input-group">
                  <label>Manual ERP Override (Only if Admin instructs)</label>
                  <input
                    type="text"
                    placeholder="Paste exact Factory ID"
                    value={editForm.erpPartyId}
                    onChange={(e) =>
                      setEditForm({ ...editForm, erpPartyId: e.target.value })
                    }
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-save" disabled={syncing}>
                    {syncing ? "Saving..." : "Save Profile"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default RetailerProfile;
